import React from 'react';
import {
  Autocomplete,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import { GridApi } from 'ag-grid-community';
import { CompanyType } from 'graphql/types';

interface CompanyTypeAutocompleteProps {
  companyTypes: CompanyType[];
  selectedCompanyTypes: CompanyType[];
  setSelectedCompanyTypes: (types: CompanyType[]) => void;
  gridApi?: GridApi | null;
  isInCompanyForm?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

export default function CompanyTypeAutocomplete({
  companyTypes,
  selectedCompanyTypes,
  setSelectedCompanyTypes,
  gridApi,
  isInCompanyForm = false,
  disabled = false,
  error = false,
  helperText
}: CompanyTypeAutocompleteProps) {
  const handleChange = (
    event: React.SyntheticEvent,
    newValue: CompanyType[]
  ) => {
    setSelectedCompanyTypes(newValue);
    if (!isInCompanyForm) {
      gridApi?.setFilterModel({
        ...gridApi.getFilterModel(),
        type: { filterType: 'set', values: newValue }
      });
      gridApi?.onFilterChanged();
    }
  };

  return (
    <>
      <Typography variant="caption" color="secondary">
        Type
        {isInCompanyForm && ' *'}
      </Typography>
      <Autocomplete<CompanyType, true, false, false>
        multiple
        id={isInCompanyForm ? "entity-type" : "companyTypes"}
        options={companyTypes.filter(
          (option) => !selectedCompanyTypes.includes(option)
        )}
        value={selectedCompanyTypes}
        onChange={handleChange}
        disabled={disabled}
        renderTags={isInCompanyForm ? (value, getTagProps) =>
          value.map((option, index) => (
            <Chip variant="combined" label={option} {...getTagProps({ index })} key={option} />
          ))
          : undefined}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={isInCompanyForm ? "filled" : "outlined"}
            size={isInCompanyForm ? "medium" : "small"}
            error={error}
            helperText={helperText}
            placeholder={
              selectedCompanyTypes.length > 0 ? '' : 'Select Company Types'
            }
            InputProps={{
              ...params.InputProps,
              sx: {
                ...(selectedCompanyTypes.length ===
                  Object.values(CompanyType).length && {
                  '.MuiAutocomplete-input': {
                    display: 'none'
                  }
                })
              }
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {

              '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
                fontSize: isInCompanyForm ? '14px' : '12px'
              }
            }
          }
        }}
        sx={{

          minWidth: isInCompanyForm ? '100%' : 200,
          '& .MuiOutlinedInput-root': {
            height: isInCompanyForm ? 'auto' : 32.5,
            whiteSpace: 'nowrap',
            flexWrap: 'nowrap',
          },
          '& .MuiFilledInput-root': {
            paddingTop: '12px !important',
            paddingBottom: '7px'
          },
          '& .MuiChip-root': {
            height: isInCompanyForm ? 30 : 20,
            fontSize: isInCompanyForm ? '12px' : '10px',
          },
          '& .MuiAutocomplete-input': {
            padding: '0 4px'
          },
          '& .MuiChip-deleteIcon, & .MuiAutocomplete-clearIndicator svg': {
            fontSize: 17,
            width: 17,
            height: 17
          }
        }}
      />
    </>
  );
}
